import { EllipseCurve, Group, Vector3 } from "three";
import { Consts } from "../../Utils/Consts.js";
import { VectorUtils } from "../../Utils/VectorUtils.js";
import { CalibrationCircle } from "./CalibrationCircle.js";
import { CalibrationEllipse } from "./CalibrationEllipse.js";
import { CalibrationLine } from "./CalibrationLine.js";
export var CalibrationMode;
(function (CalibrationMode) {
    CalibrationMode[CalibrationMode["line"] = 0] = "line";
    CalibrationMode[CalibrationMode["circle"] = 1] = "circle";
    CalibrationMode[CalibrationMode["ellipse"] = 2] = "ellipse";
    CalibrationMode[CalibrationMode["none"] = 3] = "none";
})(CalibrationMode || (CalibrationMode = {}));
/**
 * Tool that allow to calculate the scale factor.
 */
export class CalibrationTool extends Group {
    get mode() { return this.tool?.mode ?? CalibrationMode.none; }
    set mode(value) {
        this.tool?.removeFromParent();
        if (value === CalibrationMode.none) {
            this.tool = null;
            return;
        }
        if (!this._tools[value]) {
            this._tools[value] = new this._toolTypes[value](this._viewType);
        }
        this.tool = this._tools[value];
        this.add(this.tool);
        this.tool.start();
    }
    constructor(viewType, plane) {
        super();
        this.tool = null;
        this._tools = [];
        this._toolTypes = [CalibrationLine, CalibrationCircle, CalibrationEllipse];
        this._viewType = viewType;
        this._plane = plane;
    }
    /**
     * Get scale factor based according to registered points.
     */
    getScaleFactor(realLength) {
        return this.tool.getScaleFactor(realLength);
    }
    setCirclePoints(points) {
        if (this.areCirclePointsValid(points)) {
            this.mode = CalibrationMode.circle;
            this.tool.onRegSuccess(points);
        }
        else {
            this.dispatchEvent({ type: 'circlePointsError' });
            this.mode = CalibrationMode.none;
        }
    }
    areCirclePointsValid(points) {
        const circle = this.getCircleFromThreePoints(points);
        if (!circle)
            return false;
        const vertices = this._plane.getVertices();
        const planeCenter = vertices.A.clone().lerp(vertices.C, 0.5); // the plane is always in the origin theoretically
        // we create more virtual points for a better check
        const { radius, centerX, centerY } = circle;
        const virtualPoints = new EllipseCurve(centerX, centerY, radius, radius, 0, 2 * Math.PI, false, 0).getPoints(20).map(x => new Vector3(x.x, x.y, 0));
        return VectorUtils.arePointsOnSameSide(vertices.A, vertices.B, Consts.planeNormal, planeCenter, ...virtualPoints) &&
            VectorUtils.arePointsOnSameSide(vertices.B, vertices.C, Consts.planeNormal, planeCenter, ...virtualPoints) &&
            VectorUtils.arePointsOnSameSide(vertices.C, vertices.D, Consts.planeNormal, planeCenter, ...virtualPoints) &&
            VectorUtils.arePointsOnSameSide(vertices.D, vertices.A, Consts.planeNormal, planeCenter, ...virtualPoints);
    }
    getCircleFromThreePoints(points) {
        if (points.length < 3)
            return null;
        const [p1, p2, p3] = points;
        const x1 = p1.x;
        const y1 = p1.y;
        const x2 = p2.x;
        const y2 = p2.y;
        const x3 = p3.x;
        const y3 = p3.y;
        const d = 2 * (x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2));
        if (Math.abs(d) < Number.EPSILON)
            return null;
        const centerX = ((x1 * x1 + y1 * y1) * (y2 - y3) + (x2 * x2 + y2 * y2) * (y3 - y1) + (x3 * x3 + y3 * y3) * (y1 - y2)) / d;
        const centerY = ((x1 * x1 + y1 * y1) * (x3 - x2) + (x2 * x2 + y2 * y2) * (x1 - x3) + (x3 * x3 + y3 * y3) * (x2 - x1)) / d;
        const radius = Math.hypot(x1 - centerX, y1 - centerY);
        if (!Number.isFinite(radius) || radius <= 0)
            return null;
        return { radius, centerX, centerY };
    }
}
//# sourceMappingURL=CalibrationTool.js.map